import React, { useState, useEffect, useCallback, useRef } from "react";
import DivTag from "../Components/DivComponent/DivTag";
import ReportHeading from "../Components/ReportHeadings/ReportHeadings";
import { motion } from "framer-motion";
import Seo from "../../components/SeoComponent/Seo";
import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";
import { ToastContainer } from "react-toastify";
import { FaEye, FaSearch, FaSync } from "react-icons/fa";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import Loader from "../../components/Loader/Loader";
import { MobileDeviceLockReportService,ActionListService,ApplyActionService,GetActionService,StatusActionService } from "../../services/MobileDeviceLockService/MobileDeviceLockService";
import DeviceModal from "./DeviceModal";

const MobileDeviceLockReport = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState([]);
  const [selectedDevice, setSelectedDevice] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [pagination, setPagination] = useState({
    total: 0,
    per_page: 10,
    current_page: 1,
    last_page: 1,
  });
  const [filters, setFilters] = useState({
    status: "",
    running: "",
    search: "",
    user_id: "",
  });

  // Ref for debounce timer
  const debounceTimerRef = useRef(null);

  const fetchData = async (page = 1, perPage = 10) => {
    setIsLoading(true);
    try {
      const response = await MobileDeviceLockReportService({
        ...filters,
        per_page: perPage,
        page: page,
      });

      if (response?.status?.code === 200) {
        setData(response.data || []);
        setPagination({
          total: response.pagination?.total || response.data?.length || 0,
          per_page: perPage,
          current_page: page,
          last_page: response.pagination?.last_page || Math.ceil((response.data?.length || 0) / perPage),
        });
        //showSuccessToast(response.message || "Data fetched successfully");
      } else {
        showErrorToast("Failed to fetch data");
        setData([]);
      }
    } catch (error) {
      showErrorToast(error?.message || "An error occurred");
      setData([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData(pagination.current_page, pagination.per_page);
  }, []);

  // Debounced search function
  const debouncedSearch = useCallback((searchValue) => {
    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Only trigger search if 3 or more characters
    if (searchValue.length >= 3) {
      debounceTimerRef.current = setTimeout(() => {
        fetchData(1, pagination.per_page);
      }, 500); // 500ms delay
    } else if (searchValue.length === 0) {
      // If search is cleared, fetch all data
      debounceTimerRef.current = setTimeout(() => {
        fetchData(1, pagination.per_page);
      }, 500);
    }
  }, [pagination.per_page]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));

    // Trigger debounced search only for search field
    if (key === "search") {
      debouncedSearch(value);
    }
  };

  const applyFilters = () => {
    // Clear any pending debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    fetchData(1, pagination.per_page);
  };

  const resetFilters = () => {
    // Clear debounce timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setFilters({ status: "", running: "", search: "", user_id: "" });
    setTimeout(() => fetchData(1, pagination.per_page), 100);
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.last_page) {
      fetchData(newPage, pagination.per_page);
    }
  };

  const handlePerPageChange = (e) => {
    fetchData(1, parseInt(e.target.value));
  };

  const openModal = (device) => {
    setSelectedDevice(device);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDevice(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const getStatusColor = (status) => {
    const colors = {
      CREATED: "bg-blue-100 text-blue-800",
      INITIATED: "bg-yellow-100 text-yellow-800",
      ACTIVE: "bg-green-100 text-green-800",
      PENDING: "bg-orange-100 text-orange-800",
      FINISHED: "bg-gray-100 text-gray-800",
      FAILED: "bg-red-100 text-red-800",
    };
    return colors[status] || "bg-gray-100 text-gray-800";
  };


  return (
    <motion.div initial={{ opacity: 0, y: -50 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
      <Seo title="Mobile Device Lock Report" />
      {isLoading && <Loader />}

      <DivTag className="mt-10 bg-white rounded-lg shadow">
        <ReportHeading name="Mobile Device Lock Report" />

        {/* Compact Filters */}
        <div className="p-3 border-b mt-3">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-6">
            <select 
              value={filters.status} 
              onChange={(e) => handleFilterChange("status", e.target.value)} 
              className="px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="PENDING">Pending</option>
              <option value="ACTIVE">Active</option>
              <option value="FINISHED">Finished</option>
              <option value="FAILED">Failed</option>
              <option value="CREATED">Created</option>
              <option value="INITIATED">Initiated</option>
            </select>

            <select 
              value={filters.running} 
              onChange={(e) => handleFilterChange("running", e.target.value)} 
              className="px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500"
            >
              <option value="">Running Status</option>
              <option value="true">Running</option>
              <option value="false">Not Running</option>
            </select>

            <input 
              type="text" 
              value={filters.user_id} 
              onChange={(e) => handleFilterChange("user_id", e.target.value)} 
              placeholder="User ID" 
              className="px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500" 
            />

            <div className="relative">
              <input 
                type="text" 
                value={filters.search} 
                onChange={(e) => handleFilterChange("search", e.target.value)} 
                placeholder="Search (min 3 chars)..." 
                className="w-full px-2 py-1 text-sm border rounded focus:ring-1 focus:ring-blue-500" 
              />
              {filters.search.length > 0 && filters.search.length < 3 && (
                <span className="absolute text-xs text-orange-500 -bottom-4 left-1">
                  Type {3 - filters.search.length} more char{3 - filters.search.length > 1 ? 's' : ''}
                </span>
              )}
            </div>

            <button 
              onClick={applyFilters} 
              className="flex items-center justify-center px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700"
            >
              <FaSearch className="mr-1" /> Search
            </button>

            <button 
              onClick={resetFilters} 
              className="flex items-center justify-center px-3 py-1 text-sm text-gray-700 bg-gray-200 rounded hover:bg-gray-300"
            >
              <FaSync className="mr-1" /> Reset
            </button>
          </div>
        </div>

        {/* Compact Table */}
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm divide-y">
            <thead className="bg-blue-700 text-white">
              <tr>
                <th className="px-3 py-2 text-xs font-medium text-left text-white uppercase">ID</th>
                <th className="px-3 py-2 text-xs font-medium text-left text-white uppercase">Name</th>
                <th className="px-3 py-2 text-xs font-medium text-left text-white uppercase">Mobile</th>
                <th className="px-3 py-2 text-xs font-medium text-left text-white uppercase">Device</th>
                <th className="px-3 py-2 text-xs font-medium text-left text-white uppercase">Status</th>
                <th className="px-3 py-2 text-xs font-medium text-left text-white uppercase">Running</th>
                <th className="px-3 py-2 text-xs font-medium text-left text-white uppercase">Created</th>
                <th className="px-3 py-2 text-xs font-medium text-center text-white uppercase">Action</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y">
              {data.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-3 py-6 text-center text-gray-500">No records found</td>
                </tr>
              ) : (
                data.map((item) => (
                  <tr key={item.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2 text-gray-900">{item.id}</td>
                    <td className="px-3 py-2 text-gray-900">{item.name || "N/A"}</td>
                    <td className="px-3 py-2 text-gray-900">{item.mobile_number || "N/A"}</td>
                    <td className="px-3 py-2 text-gray-700">{item.manufacturer} {item.model}</td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(item.status || item.key_status)}`}>
                        {item.status || item.key_status || "N/A"}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <span className={`px-2 py-0.5 text-xs rounded-full ${item.running ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}`}>
                        {item.running ? "Yes" : "No"}
                      </span>
                    </td>
                    <td className="px-3 py-2 text-gray-700 whitespace-nowrap">{formatDate(item.created_at)}</td>
                    <td className="px-3 py-2 text-center">
                      <button onClick={() => openModal(item)} className="text-blue-600 hover:text-blue-800">
                        <FaEye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Compact Pagination */}
        <div className="flex items-center justify-between px-3 py-2 border-t">
          <div className="text-xs text-gray-700">
            Showing {(pagination.current_page - 1) * pagination.per_page + 1} to {Math.min(pagination.current_page * pagination.per_page, pagination.total)} of {pagination.total}
          </div>

          <div className="flex items-center gap-2">
            <select value={pagination.per_page} onChange={handlePerPageChange} className="px-2 py-1 text-xs border rounded">
              <option value="10">10</option>
              <option value="25">25</option>
              <option value="50">50</option>
              <option value="100">100</option>
            </select>

            <button onClick={() => handlePageChange(pagination.current_page - 1)} disabled={pagination.current_page === 1} className="p-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              <MdChevronLeft className="w-4 h-4" />
            </button>

            <span className="text-xs">Page {pagination.current_page} of {pagination.last_page}</span>

            <button onClick={() => handlePageChange(pagination.current_page + 1)} disabled={pagination.current_page === pagination.last_page} className="p-1 border rounded hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed">
              <MdChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>

        <ToastContainer />
      </DivTag>

      {/* Modal Component */}
      {isModalOpen && <DeviceModal device={selectedDevice} onClose={closeModal} formatDate={formatDate} getStatusColor={getStatusColor} />}
    </motion.div>
  );
};

export default MobileDeviceLockReport;
